/*
Confirmation dialog for deleting a capture. Mirrors Android's
DeleteCaptureDialog: a fixed title ("Supprimer cette photo ?"), an
adaptive body that warns about re-locking the species/remarquable
when this is the last capture of its entity, and two buttons
(Supprimer / Annuler).

The dialog is mounted as a one-off DOM tree under `document.body`
(same pattern as `openLightbox`) so it sits above any clipping
ancestor. The returned Promise resolves `true` if the user
confirmed, `false` if they cancelled (Annuler, backdrop tap,
Escape key).

A subtle haptic fires on open so a long-press → dialog feels like
the Compose press-hold + dialog beat on Android.
*/

export type DeleteCaptureKind = 'species' | 'remarquable'

export interface DeleteCaptureDialogArgs
{
	kind: DeleteCaptureKind  // governs the "last for species" vs "last for remarquable" wording in `isLast` mode
	displayName: string  // species name or remarquable label inserted in the bold body
	isLast: boolean  // true when this capture is the last one for its entity — toggles the re-lock copy
}

export function confirmDeleteCapture(inArgs: DeleteCaptureDialogArgs): Promise<boolean>
{
	return new Promise<boolean>((inResolve) => {
		const vRoot = document.createElement('div')
		vRoot.className = 'dialog'
		vRoot.setAttribute('role', 'dialog')
		vRoot.setAttribute('aria-modal', 'true')
		vRoot.setAttribute('aria-labelledby', 'dialog-title')

		const vBodyHtml = renderBody(inArgs)  // resolved once, never refreshed (dialog is single-shot)

		vRoot.innerHTML = `
			<div class="dialog-backdrop"></div>
			<div class="dialog-panel">
				<h2 class="display dialog-title" id="dialog-title">Supprimer cette photo ?</h2>
				<div class="dialog-body">${vBodyHtml}</div>
				<div class="dialog-actions">
					<button class="dialog-button" id="dialog-cancel">Annuler</button>
					<button class="dialog-button dialog-button-danger" id="dialog-confirm">Supprimer</button>
				</div>
			</div>
		`
		document.body.appendChild(vRoot)
		const vPrevOverflow = document.body.style.overflow  // restore on close
		document.body.style.overflow = 'hidden'

		// Discrete haptic on open — mirrors Compose's LongPress + dialog
		// confirmation beat. Soft-fail on iOS Safari where vibrate is a no-op.
		if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function')
		{
			try { navigator.vibrate(15) } catch { /* sandboxed PWA edge case */ }
		}

		const vSettle = (inAnswer: boolean): void => {
			document.body.style.overflow = vPrevOverflow
			document.removeEventListener('keydown', vOnKey)
			vRoot.remove()
			inResolve(inAnswer)
		}

		const vOnKey = (inEv: KeyboardEvent): void => {
			if (inEv.key === 'Escape') vSettle(false)
			else if (inEv.key === 'Enter') vSettle(true)
		}
		document.addEventListener('keydown', vOnKey)

		vRoot.querySelector('.dialog-backdrop')?.addEventListener('click', () => vSettle(false))
		vRoot.querySelector('#dialog-cancel')?.addEventListener('click', () => vSettle(false))
		vRoot.querySelector('#dialog-confirm')?.addEventListener('click', () => vSettle(true))
	})
}

function renderBody(inArgs: DeleteCaptureDialogArgs): string
{
	const vEscapedName = escapeHtml(inArgs.displayName)
	if (inArgs.isLast)
	{
		// Wording mirrors Android: re-lock warning depends on kind.
		const vEntityLabel = inArgs.kind === 'remarquable' ? 'son entrée Remarquable' : 'son entrée dans ton Arboretum'
		return `C'est ta dernière capture de <strong>${vEscapedName}</strong>. La supprimer va re-verrouiller ${vEntityLabel}.`
	}
	return 'Cette capture sera retirée de ta galerie. Action définitive.'
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
